/** @jsx jsx */
/** @jsxRuntime classic */
import { jsx } from '@emotion/react';
import React, { ChangeEvent, useCallback, useRef } from 'react';
import InputOutline from '../BaseInput/InputOutline';
import Icon from '../icons/Icon';
import InputControl from '../InputControl/InputControl';
import useTheme from '../style/useTheme';
import './Select.scss';

type Option = { value: string; label?: string };

export type Props = {
  label?: string;
  onChange: (value: string, option: Option) => void;
  options: Option[];
  value?: Option;
  ariaLabel?: string;
  required?: boolean;
  id?: string;
};

const ID = 'Select-select';

const DELINEATOR = '___';

const convertOptionToValueString = (option: Option) =>
  `${option.value}${DELINEATOR}${option.label || ''}`;

const convertValueStringToOption = (valueString: string): Option => {
  const [value, label] = valueString.split(DELINEATOR);
  return { value, label };
};

const Select: React.FC<Props> = ({
  label,
  onChange,
  options,
  ariaLabel,
  value,
  required,
  id = ID,
}) => {
  const { theme, resolveColor, resolveTypography } = useTheme();
  const ref = useRef<HTMLSelectElement>(null);
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      event.persist();
      const selectedOption = convertValueStringToOption(event.target.value);
      const actualValue = selectedOption.value;
      onChange(actualValue, selectedOption);
    },
    [onChange]
  );
  return (
    <InputControl label={label} id={id} required={required}>
      <InputOutline className="Select-container">
        <select
          ref={ref}
          css={{
            ...resolveTypography('body'),
            border: 'none',
            height: '48px',
            outline: 'none',
            backgroundColor: 'transparent',
            color: resolveColor(theme.components.input['text-color']),
            flex: 1,
            position: 'relative',
            cursor: 'pointer',
            transition: `all ${theme.animations.duration.standard}ms ${theme.animations.easing.standard}`,
            appearance: 'none', // Remove any default Select styling
            '-webkit-appearance': 'none', // For safari
            '-moz-appearance': 'none', // For firefox
            '-ms-apperance': 'none', // For IE
            '::-ms-expand': {
              display: 'none',
            },
            textOverflow: 'ellipsis',
            width: '100%', // To prevent overflow, this forces the select to be the width of its parent
          }}
          onChange={handleChange}
          data-testid={ID}
          id={ID}
          aria-label={ariaLabel}
          value={value ? convertOptionToValueString(value) : undefined}
        >
          <option value="default" data-testid="Select-option-defualt">
            {' '}
          </option>
          {options.map((option) => {
            const valueString = convertOptionToValueString(option);
            return (
              <option
                key={valueString}
                value={valueString}
                data-testid={`Select-option-${option.value}`}
                aria-label={`${ariaLabel || 'Select-option'}-${option.value}`}
              >
                {option.label || option.value}
              </option>
            );
          })}
        </select>
        <Icon
          sx={{
            flexBasis: 'auto',
            pointerEvents: 'none',
          }}
          icon="caret"
        />
      </InputOutline>
    </InputControl>
  );
};

export default Select;
